import React, { useState, useEffect } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ['All categories', 'Digitalisation', 'Growth', 'Integrated logistics', 'Resilience', 'Sustainability'];

const InsightsSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('All categories');
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, [selectedCategory]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'All categories' 
        ? `${API}/insights`
        : `${API}/insights?category=${encodeURIComponent(selectedCategory)}`;
      
      const response = await axios.get(url);
      if (response.data.success) {
        setInsights(response.data.insights);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6">
        <h2 className="text-3xl lg:text-4xl font-bold text-[#0A1F3B] mb-8">
          Latest insights
        </h2>
        <p className="text-gray-600 mb-8 max-w-3xl">
          Access the latest articles, trends and research to guide your strategy and keep you updated on what's new in the supply chain world.
        </p>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-12">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(category)}
              className={selectedCategory === category ? 'bg-[#0A1F3B] hover:bg-[#0A1F3B]/90' : ''}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#00ACC1]" />
          </div>
        ) : (
          <>
            {/* Insights Grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {insights.map((insight) => (
                <Card 
                  key={insight.id} 
                  className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={insight.image}
                      alt={insight.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <Badge className="mb-3 bg-[#00ACC1] hover:bg-[#0097A7]">
                      {insight.category}
                    </Badge>
                    <h3 className="text-xl font-semibold text-[#0A1F3B] mb-3 group-hover:text-[#00ACC1] transition-colors">
                      {insight.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {insight.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {new Date(insight.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      <ArrowRight className="w-5 h-5 text-[#00ACC1] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {insights.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No insights found for this category.</p>
              </div>
            )}

            <div className="text-center">
              <Button 
                variant="outline" 
                className="border-[#0A1F3B] text-[#0A1F3B] hover:bg-[#0A1F3B] hover:text-white px-8 py-6"
              >
                View All
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default InsightsSection;